/**
 * SegmentationsCard — manage named gap-matrix segmentations.
 *
 * Mounted on the ALM Parameters page (Phase 4.1.SEG / 1.36.0).
 *
 * Sections:
 *  - Standard segmentations (is_preset=TRUE, read-only, provided by the system)
 *  - Customized segmentations (is_preset=FALSE, full CRUD for ALM_ADMIN)
 *  - Inline create / edit form (one open at a time)
 *
 * The form lets the user specify:
 *  - Name + optional description
 *  - Dimension (INSTRUMENT_TYPE | BS_CLASS | CURRENCY | SENS | BS_POSITION | PCEC)
 *  - Mappings: dimension value → segment label (+ sort order)
 */
import { useState } from 'react';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Edit2, Loader2, Plus, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CubixApiError } from '@/lib/api/types';
import { useAuthStore } from '@/lib/auth/store';

import {
  useSegmentations,
  useCreateSegmentation,
  useUpdateSegmentation,
  useDeleteSegmentation,
} from './segmentationApi';
import type {
  MatrixDimension,
  MatrixSegmentation,
  MatrixSegmentationCreate,
  MatrixSegmentationMapping,
} from './segmentationApi';

// ── Helpers ───────────────────────────────────────────────────────────────────

const DIM_LABELS: Record<MatrixDimension, string> = {
  INSTRUMENT_TYPE: 'Instrument type',
  BS_CLASS:        'BS class',
  CURRENCY:        'Currency',
  SENS:            'Sens',
  BS_POSITION:     'Bilan / Hors-bilan',
  PCEC:            'Chap. PCEC',
};

const DIM_OPTIONS: Array<{ value: MatrixDimension; label: string }> = [
  { value: 'INSTRUMENT_TYPE', label: 'Instrument type' },
  { value: 'BS_CLASS',        label: 'BS class' },
  { value: 'CURRENCY',        label: 'Currency' },
  { value: 'SENS',            label: 'Sens' },
  { value: 'BS_POSITION',     label: 'Bilan / Hors-bilan' },
  { value: 'PCEC',            label: 'Chap. PCEC' },
];

const selectCls =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50';

function errorMessage(err: unknown): string {
  if (err instanceof CubixApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'An error occurred';
}

// ── Mapping editor row ────────────────────────────────────────────────────────

function MappingRow({
  mapping,
  index,
  disabled,
  onChange,
  onRemove,
}: {
  mapping: MatrixSegmentationMapping;
  index: number;
  disabled: boolean;
  onChange: (m: MatrixSegmentationMapping) => void;
  onRemove: () => void;
}) {
  return (
    <div className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_5rem_auto]">
      <div className="space-y-1">
        {index === 0 && (
          <p className="text-xs font-medium text-muted-foreground">Dimension value</p>
        )}
        <Input
          value={mapping.dimensionValue}
          disabled={disabled}
          placeholder="e.g. BOND"
          className="h-7 text-xs"
          onChange={(e) => onChange({ ...mapping, dimensionValue: e.target.value.toUpperCase() })}
        />
      </div>
      <div className="space-y-1">
        {index === 0 && (
          <p className="text-xs font-medium text-muted-foreground">Segment label</p>
        )}
        <Input
          value={mapping.segmentLabel}
          disabled={disabled}
          placeholder="e.g. Titres souverains"
          className="h-7 text-xs"
          onChange={(e) => onChange({ ...mapping, segmentLabel: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        {index === 0 && (
          <p className="text-xs font-medium text-muted-foreground">Order</p>
        )}
        <Input
          type="number"
          value={mapping.sortOrder ?? 0}
          disabled={disabled}
          min={0}
          className="h-7 text-xs text-right"
          onChange={(e) => onChange({ ...mapping, sortOrder: Number(e.target.value) || 0 })}
        />
      </div>
      <div className={index === 0 ? 'mt-4' : ''}>
        {!disabled && (
          <button
            type="button"
            onClick={onRemove}
            className="text-muted-foreground hover:text-destructive transition-colors"
            title="Remove mapping"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Inline segmentation form ──────────────────────────────────────────────────

interface SegFormState {
  name: string;
  description: string;
  dimension: MatrixDimension;
  mappings: MatrixSegmentationMapping[];
}

function emptyForm(): SegFormState {
  return { name: '', description: '', dimension: 'INSTRUMENT_TYPE', mappings: [] };
}

function segToForm(seg: MatrixSegmentation): SegFormState {
  return {
    name:        seg.name,
    description: seg.description ?? '',
    dimension:   seg.dimension,
    mappings:    seg.mappings.map((m) => ({ ...m })),
  };
}

function SegmentationForm({
  initial,
  onSave,
  onCancel,
  isPending,
  error,
}: {
  initial: SegFormState;
  onSave: (data: MatrixSegmentationCreate) => void;
  onCancel: () => void;
  isPending: boolean;
  error: unknown;
}) {
  const [form, setForm] = useState<SegFormState>(initial);

  function patch(p: Partial<SegFormState>) {
    setForm((f) => ({ ...f, ...p }));
  }

  function patchMapping(idx: number, m: MatrixSegmentationMapping) {
    const next = [...form.mappings];
    next[idx] = m;
    patch({ mappings: next });
  }

  function addMapping() {
    patch({
      mappings: [
        ...form.mappings,
        { dimensionValue: '', segmentLabel: '', sortOrder: form.mappings.length },
      ],
    });
  }

  function removeMapping(idx: number) {
    patch({ mappings: form.mappings.filter((_, i) => i !== idx) });
  }

  function handleSave() {
    // Basic validation
    if (!form.name.trim()) return;
    const clean = form.mappings.filter(
      (m) => m.dimensionValue.trim() && m.segmentLabel.trim(),
    );
    onSave({
      name:        form.name.trim(),
      description: form.description.trim() || undefined,
      dimension:   form.dimension,
      mappings:    clean,
    });
  }

  return (
    <div className="rounded-md border bg-muted/10 p-4 space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="sf-name">Name <span className="text-destructive">*</span></Label>
          <Input
            id="sf-name"
            value={form.name}
            onChange={(e) => patch({ name: e.target.value })}
            placeholder="e.g. BAM Standard"
            maxLength={100}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sf-dim">Dimension <span className="text-destructive">*</span></Label>
          <select
            id="sf-dim"
            value={form.dimension}
            onChange={(e) => patch({ dimension: e.target.value as MatrixDimension })}
            className={selectCls}
          >
            {DIM_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="sf-desc">Description</Label>
          <Input
            id="sf-desc"
            value={form.description}
            onChange={(e) => patch({ description: e.target.value })}
            placeholder="Optional — shown in the matrix picker"
            maxLength={255}
          />
        </div>
      </div>

      {/* Mappings */}
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Dimension value → label mappings
        </p>
        {form.mappings.length === 0 ? (
          <p className="rounded border bg-background p-2 text-xs text-muted-foreground">
            No mappings yet — all dimension values will be shown as-is.
            Add a mapping to group or rename values.
          </p>
        ) : (
          <div className="space-y-1.5">
            {form.mappings.map((m, i) => (
              <MappingRow
                key={i}
                mapping={m}
                index={i}
                disabled={false}
                onChange={(updated) => patchMapping(i, updated)}
                onRemove={() => removeMapping(i)}
              />
            ))}
          </div>
        )}
        <Button type="button" variant="outline" size="sm" onClick={addMapping}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Add mapping
        </Button>
      </div>

      {error ? (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {errorMessage(error)}
        </div>
      ) : null}

      <div className="flex items-center gap-2 pt-1">
        <Button type="button" size="sm" onClick={handleSave} disabled={isPending || !form.name.trim()}>
          {isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
          Save segmentation
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          <X className="mr-1 h-3.5 w-3.5" /> Cancel
        </Button>
      </div>
    </div>
  );
}

// ── Segmentation row ──────────────────────────────────────────────────────────

function SegmentationRow({
  seg,
  isAdmin,
  onEdit,
  onDelete,
  isDeleting,
}: {
  seg: MatrixSegmentation;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-md border bg-background">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-muted-foreground hover:text-foreground transition-colors"
          title={expanded ? 'Collapse' : 'Expand mappings'}
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{seg.name}</span>
            {seg.isPreset && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-200">
                Standard
              </span>
            )}
            <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
              {DIM_LABELS[seg.dimension] ?? seg.dimension}
            </span>
            <span className="text-xs text-muted-foreground">
              {seg.mappings.length} mapping{seg.mappings.length !== 1 ? 's' : ''}
            </span>
          </div>
          {seg.description ? (
            <p className="text-xs text-muted-foreground mt-0.5">{seg.description}</p>
          ) : null}
        </div>

        {isAdmin && !seg.isPreset && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onEdit}
              title="Edit"
              className="h-7 w-7 p-0"
            >
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDelete}
              disabled={isDeleting}
              title="Delete"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
            >
              {isDeleting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Expanded mapping list */}
      {expanded && seg.mappings.length > 0 && (
        <div className="border-t px-3 py-2">
          <div className="grid grid-cols-[1fr_1fr_4rem] gap-x-2 gap-y-0.5 text-xs">
            <span className="font-medium text-muted-foreground">Dimension value</span>
            <span className="font-medium text-muted-foreground">Segment label</span>
            <span className="font-medium text-muted-foreground text-right">Order</span>
            {seg.mappings
              .slice()
              .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
              .map((m, i) => (
                <>
                  <span key={`dv-${i}`} className="font-mono">{m.dimensionValue}</span>
                  <span key={`sl-${i}`}>{m.segmentLabel}</span>
                  <span key={`so-${i}`} className="text-right text-muted-foreground">{m.sortOrder ?? 0}</span>
                </>
              ))}
          </div>
        </div>
      )}
      {expanded && seg.mappings.length === 0 && (
        <p className="border-t px-3 py-2 text-xs text-muted-foreground">
          No mappings — dimension values shown as-is.
        </p>
      )}
    </div>
  );
}

// ── Main card ─────────────────────────────────────────────────────────────────

type EditState =
  | { mode: 'none' }
  | { mode: 'create' }
  | { mode: 'edit'; seg: MatrixSegmentation };

export function SegmentationsCard() {
  const isAdmin = useAuthStore((s) => s.hasRole('ALM_ADMIN'));

  const { data: segs, isLoading, isError, error: loadError } = useSegmentations();
  const createMut = useCreateSegmentation();
  const updateMut = useUpdateSegmentation();
  const deleteMut = useDeleteSegmentation();

  const [editState, setEditState] = useState<EditState>({ mode: 'none' });
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  function handleCreate(data: MatrixSegmentationCreate) {
    createMut.mutate(data, {
      onSuccess: () => setEditState({ mode: 'none' }),
    });
  }

  function handleUpdate(data: MatrixSegmentationCreate & { id: string }) {
    updateMut.mutate(data, {
      onSuccess: () => setEditState({ mode: 'none' }),
    });
  }

  function handleDelete(id: string) {
    if (!window.confirm('Delete this segmentation? This cannot be undone.')) return;
    deleteMut.mutate(id, {
      onSuccess: () => {
        setDeleteSuccess(true);
        setTimeout(() => setDeleteSuccess(false), 3000);
      },
    });
  }

  const presets  = (segs ?? []).filter((s) => s.isPreset);
  const customs  = (segs ?? []).filter((s) => !s.isPreset);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Matrix Segmentations</CardTitle>
            <CardDescription>
              Named groupings of dimension values used by the Liquidity Gap matrix.
              <strong> Standard</strong> segmentations are system-provided presets (<code>is_preset = TRUE</code>) and cannot be modified or deleted.
              ALM_ADMIN can create <strong>Customized</strong> segmentations (<code>is_preset = FALSE</code>) with full CRUD.
            </CardDescription>
          </div>
          {isAdmin && editState.mode === 'none' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditState({ mode: 'create' })}
              className="shrink-0"
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> New segmentation
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading segmentations…
          </div>
        )}

        {isError && loadError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {errorMessage(loadError)}
          </div>
        )}

        {/* Create form */}
        {editState.mode === 'create' && (
          <SegmentationForm
            initial={emptyForm()}
            onSave={handleCreate}
            onCancel={() => { setEditState({ mode: 'none' }); createMut.reset(); }}
            isPending={createMut.isPending}
            error={createMut.isError ? createMut.error : null}
          />
        )}

        {/* Standard segmentations (is_preset = TRUE) */}
        {presets.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Standard segmentations
            </p>
            {presets.map((seg) => (
              <div key={seg.id}>
                {editState.mode === 'edit' && editState.seg.id === seg.id ? (
                  <SegmentationForm
                    initial={segToForm(seg)}
                    onSave={(d) => handleUpdate({ id: seg.id, ...d })}
                    onCancel={() => { setEditState({ mode: 'none' }); updateMut.reset(); }}
                    isPending={updateMut.isPending}
                    error={updateMut.isError ? updateMut.error : null}
                  />
                ) : (
                  <SegmentationRow
                    seg={seg}
                    isAdmin={isAdmin}
                    onEdit={() => setEditState({ mode: 'edit', seg })}
                    onDelete={() => handleDelete(seg.id)}
                    isDeleting={deleteMut.isPending && deleteMut.variables === seg.id}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Customized segmentations (is_preset = FALSE) */}
        {customs.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Customized segmentations
            </p>
            {customs.map((seg) => (
              <div key={seg.id}>
                {editState.mode === 'edit' && editState.seg.id === seg.id ? (
                  <SegmentationForm
                    initial={segToForm(seg)}
                    onSave={(d) => handleUpdate({ id: seg.id, ...d })}
                    onCancel={() => { setEditState({ mode: 'none' }); updateMut.reset(); }}
                    isPending={updateMut.isPending}
                    error={updateMut.isError ? updateMut.error : null}
                  />
                ) : (
                  <SegmentationRow
                    seg={seg}
                    isAdmin={isAdmin}
                    onEdit={() => setEditState({ mode: 'edit', seg })}
                    onDelete={() => handleDelete(seg.id)}
                    isDeleting={deleteMut.isPending && deleteMut.variables === seg.id}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {!isLoading && !isError && presets.length === 0 && customs.length === 0 && editState.mode !== 'create' && (
          <p className="rounded border bg-muted/20 p-3 text-sm text-muted-foreground">
            No segmentations yet.{' '}
            {isAdmin ? 'Click "New segmentation" to create one.' : 'Contact an ALM_ADMIN to create segmentations.'}
          </p>
        )}

        {deleteMut.isError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {errorMessage(deleteMut.error)}
          </div>
        )}

        {deleteSuccess && (
          <p className="flex items-center gap-1.5 text-sm text-emerald-600">
            <CheckCircle2 className="h-4 w-4" /> Segmentation deleted.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
